const DEFAULT_BLOCK_TIME_MS = 12_000;
const MIN_HTTP_POLL_MS = 250;
const MAX_HTTP_POLL_MS = 12_000;
const HTTP_POLL_RATIO = 0.8;

export const normalizeBlockTime = (blockTimeMs, fallbackMs = DEFAULT_BLOCK_TIME_MS) => {
  const value = Number(blockTimeMs);
  const fallback = Number(fallbackMs);

  if (Number.isFinite(value) && value > 0) {
    return Math.round(value);
  }

  return Number.isFinite(fallback) && fallback > 0
    ? Math.round(fallback)
    : DEFAULT_BLOCK_TIME_MS;
};

export const getRpcUpdateInterval = ({
  blockTimeMs,
  minIntervalMs = MIN_HTTP_POLL_MS,
  maxIntervalMs = MAX_HTTP_POLL_MS,
  ratio = HTTP_POLL_RATIO,
  fallbackMs = DEFAULT_BLOCK_TIME_MS,
} = {}) => {
  const normalized = normalizeBlockTime(blockTimeMs, fallbackMs);
  const rawInterval = Math.floor(normalized * ratio);
  const min = normalizeBlockTime(minIntervalMs, MIN_HTTP_POLL_MS);
  const max = normalizeBlockTime(maxIntervalMs, MAX_HTTP_POLL_MS);

  return Math.min(Math.max(rawInterval, min), max);
};

export const calculateObservedBlockTime = ({
  latestBlockNumber,
  latestBlockTimestamp,
  comparisonBlockNumber,
  comparisonBlockTimestamp,
} = {}) => {
  const blockDelta = Number(latestBlockNumber) - Number(comparisonBlockNumber);
  const timestampDeltaSeconds = Number(latestBlockTimestamp) - Number(comparisonBlockTimestamp);

  if (!Number.isFinite(blockDelta) || !Number.isFinite(timestampDeltaSeconds)) {
    return null;
  }

  if (blockDelta <= 0 || timestampDeltaSeconds <= 0) {
    return null;
  }

  return Math.round((timestampDeltaSeconds * 1000) / blockDelta);
};

export const estimateObservedBlockTime = async (
  provider,
  latestBlockNumber,
  fallbackBlockTimeMs = DEFAULT_BLOCK_TIME_MS
) => {
  if (!provider || !latestBlockNumber || latestBlockNumber <= 1) {
    return null;
  }

  const fallback = normalizeBlockTime(fallbackBlockTimeMs);
  const sampleSize = fallback <= 500 ? 100 : fallback <= 2_000 ? 40 : 10;
  const comparisonBlockNumber = Math.max(1, latestBlockNumber - sampleSize);

  try {
    const [latestBlock, comparisonBlock] = await Promise.all([
      provider.getBlock(latestBlockNumber),
      provider.getBlock(comparisonBlockNumber),
    ]);

    if (!latestBlock || !comparisonBlock) {
      return null;
    }

    return calculateObservedBlockTime({
      latestBlockNumber,
      latestBlockTimestamp: latestBlock.timestamp,
      comparisonBlockNumber,
      comparisonBlockTimestamp: comparisonBlock.timestamp,
    });
  } catch (error) {
    console.warn('Failed to estimate observed block time:', error);
    return null;
  }
};

export const formatIntervalSeconds = (milliseconds) => {
  const normalized = normalizeBlockTime(milliseconds);
  return `${(normalized / 1000).toFixed(2)}s`;
};
